// src/dq/SdhReviewWorkspace_v2.tsx
// Field-centric SME review surface.
//
// WHAT CHANGED FROM THE FIRST v2 (and why)
// ────────────────────────────────────────
// The first cut rebuilt the rule pane with its own inline-styled cards. That was rejected, and
// rightly: it forked the review surface. Every decision affordance the SME actually uses —
// decide popover, bulk apply, impact preview, refine, versions, filter chips, the decision
// tray — lived only in v1, so v2 was a read-only shadow that would rot the moment v1 moved.
//
// This version owns NO rule rendering at all. It is a shell:
//
//   ┌ table strip — AUSP · MARA · MARC · MVKE ────────────────────────────────┐
//   ├ field list ────────────────┬ <ReviewCockpit fieldFilter={…} /> ─────────┤
//   │ the SME's spine: she works │ v1, unchanged, scoped to the chosen field. │
//   │ field by field. Each card  │ Its facet rail is the SAME markup, moved   │
//   │ carries the field's own    │ into a popover, because the left column    │
//   │ detail + Values/Knowledge/ │ now belongs to the field list.             │
//   │ Controls.                  │                                            │
//   └────────────────────────────┴────────────────────────────────────────────┘
//
// Styling is v1's: this file imports `CSS` from SdhReviewWorkspace and adds only the classes
// v1 has no equivalent for (table strip, field card, wide modal), using v1's own tokens. There
// is no inline-styled chrome left.
//
// Deleted from the first cut: ProfilePanel and LookupPanel. FieldValuesPanel already answers
// both questions — and answers them better, because it reconciles observed values against the
// client's supplied reference data, which neither of mine did.
import { SA_STORE, saOf, ruleKey } from "./setasideStore";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HelpProvider } from "./HelpKit";
import DashboardTab from "./DashboardTab";
import FieldValuesPanel from "./FieldValuesPanel";
import RuleStudioPanel from "./RuleStudioPanel";
import RuleVersionsPanel from "./RuleVersionsPanel";
import SmeImportPanel from "./SmeImportPanel";
import SmeAddRuleDrawer from "./SmeAddRuleDrawer";
import StatusGuide from "./StatusGuide";
// v1 is the source of truth for how a rule looks and how a decision is taken. These three are
// additive exports on it; v1's own behaviour is unchanged.
import { CSS, ReviewCockpit, RuleDetailModal } from "./SdhReviewWorkspace";
import { sdh_setaside,
  sdh_rules, sdh_dataProfile, sdh_ruleDetail, sdh_exportRulebook, sdh_smeTaxonomy,
  SME_ASSESSMENTS,
  type AssessmentDef, type EnrichedRule, type ProfileTable, type ProfileColumn,
} from "../api/sdhAPI";
import PoliciesView, { usePolicies, policiesOfField, FieldPolicies } from "./PoliciesView";
import { LifecycleBar, LeadershipView, TechnicalView, ActionQueue, StatusMini, ROLE_CSS, matchBucket, type Bucket } from "./RoleViews";
import {
  sdh_fieldContext, sdh_fieldContextSummary, sdh_scope, sdh_traceability,
  type FieldContext, type FieldSummary, type ScopeInfo, type Traceability,
} from "../api/sdhAPI_fieldContext";

/* ───────────────────────────── helpers ───────────────────────────── */
const fmt = (n?: number | null) => (n == null ? "—" : Number(n).toLocaleString());
const pct = (x?: number | null) => (x == null ? "—" : `${Number(x).toFixed(1)}%`);
const up = (s: any) => String(s ?? "").toUpperCase();
const fid = (t: any, c: any) => `${up(t)}::${up(c)}`;

/** The artifacts are not uniformly typed and the UI must not crash on the variance.
 *  `known_gaps` is a free-text paragraph on 16 of the 559 fields and absent on the rest —
 *  calling .join() on it took the whole page down. Everything the panels iterate or print now
 *  goes through one of these two. */
const asList = (v: any): any[] =>
  Array.isArray(v) ? v : (v === null || v === undefined || v === "" ? [] : [v]);
/* knowledge popup: plain wording for the gap types / texts of the old business-control pipeline */
const GAP_LABEL: Record<string, string> = { unresolved_semantic_role: "open question — what the field means",
  unresolved_relationship: "open question — a possible dependency", client_declared_gap: "declared by the client" };
const plainGap = (t: string) => String(t || "")
  .replace(/\s*\(CONTEXT_ONLY\)/g, "")
  .replace(/it must not become a business control without confirmation/gi, "it needs your confirmation before it can become a rule")
  .replace(/not evidence-backed/gi, "not supported by the data");

const asText = (v: any): string => {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== "").map(asText).join(" · ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/** One malformed field must not white-screen the workspace. The reviewer sees which field and
 *  which panel failed — enough to report it — and everything else keeps working. */
class PanelBoundary extends React.Component<
  { what: string; children: React.ReactNode }, { err: Error | null }> {
  constructor(props: { what: string; children: React.ReactNode }) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err: Error) { return { err }; }
  componentDidCatch(err: Error) { console.error("[SdhReviewWorkspace_v2] panel failed", err); }
  render() {
    if (this.state.err) {
      return (
        <div className="dqc-fx-boundary">
          <b>This panel could not be rendered for {this.props.what}.</b>
          <div>The rest of the workspace is unaffected. Please send this line to the team:</div>
          <div className="dqc-fx-mono">{String(this.state.err.message || this.state.err)}</div>
        </div>
      );
    }
    return this.props.children as any;
  }
}

/** Same contract as v1's usePersistedState, on its own key prefix so the two screens cannot
 *  fight over a stored value that means something different on each. */
function usePersisted<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [v, setV] = useState<T>(() => {
    try { const r = localStorage.getItem(key); return r == null ? initial : (JSON.parse(r) as T); }
    catch { return initial; }
  });
  const set = (next: T | ((p: T) => T)) => setV((prev) => {
    const val = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* private mode */ }
    return val;
  });
  return [v, set];
}

/** What kind of thing this field holds. `lov` leads because it is the one fact that decides
 *  whether an allowed-values rule on the field is even meaningful. */
type FieldKind = "lov" | "text" | "number" | "date" | "flag" | "id" | "unknown";
function kindOf(s?: FieldSummary | null, prof?: ProfileColumn | null): FieldKind {
  const role = String(s?.semantic_role || "");
  const dt = String(s?.data_type || prof?.data_type || "").toLowerCase();
  if ((s?.domain_count || 0) > 0 || (s?.declared_domains || 0) > 0
      || role === "code_or_status" || role === "unit_code") return "lov";
  if (role === "identifier_candidate") return "id";
  if (role === "date" || dt.indexOf("date") >= 0 || dt.indexOf("time") >= 0) return "date";
  if (role === "quantity" || role === "quantity_max" || role === "duration" || role === "percentage"
      || ["integer", "float", "decimal", "double", "number"].indexOf(dt) >= 0) return "number";
  if (role === "flag" || role === "boolean") return "flag";
  if (dt === "text" || role === "text") return "text";
  return "unknown";
}

/* ───────────────────────────── shared chrome ───────────────────────────── */
/** Wide modal built from v1's popover chrome (.dqc-pop-scrim / .dqc-pop / .dqc-pop-h), so it
 *  inherits v1's scrim, radius, shadow and header treatment rather than restating them. */
const Wide: React.FC<{ title: string; sub?: string; onClose: () => void; children: React.ReactNode }> =
  ({ title, sub, onClose, children }) => (
    <div className="dqc-pop-scrim" onClick={onClose}>
      <div className="dqc-pop wide" onClick={(e) => e.stopPropagation()}>
        <div className="dqc-pop-h">
          <span>{title}{sub ? <span className="sub">{sub}</span> : null}</span>
          <button className="x" onClick={onClose}>×</button>
        </div>
        <div className="dqc-fx-popb">{children}</div>
      </div>
    </div>
  );

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="dqc-fx-kv"><div className="k">{k}</div><div className="v">{children}</div></div>
);

const Non: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="dqc-empty">{children}</div>
);

/* ───────────────────────── knowledge panel (EKD) ───────────────────────── */
// Everything the enterprise-knowledge record holds for this field, ordered the way a reviewer
// reads it: what the field means and who says so, what shape it has, what governs its values,
// what it is related to, what is known to be missing, and the evidence underneath.
//
// `metadata_authority` carries the most weight — a rule resting on `declared` is a different
// proposition from one resting on the client's own dictionary.
const KnowledgePanel: React.FC<{ f: any; domains?: any[]; gaps?: any[] }> =
  ({ f, domains, gaps }) => {
    const doms = asList(domains);
    const gs = asList(gaps);
    if (!f) {
      return (
        <div>
          <Non>
            Nothing is recorded about this field in the enterprise knowledge artifact. Any rule
            on it rests on the data profile alone.
          </Non>
          {gs.length > 0 && <GapList gaps={gs} />}
        </div>
      );
    }
    const roles = asList(f.semantic_roles);
    const ev = asList(f.evidence);
    const linked = asList(f.linked_fields);
    const domIds = asList(f.reference_domain_ids);
    const auth = String(f.metadata_authority || "unknown");
    const authCls = auth === "dictionary_confirmed" ? "ok" : auth === "dictionary_reconciled" ? "warn" : "";
    return (
      <div>
        <KV k="Business name">
          {f.display_name || "—"}
          <span className={"dqc-fx-tag " + authCls}
            title="Where this name came from. A client dictionary confirming it is stronger evidence than the pipeline declaring it.">
            {auth}
          </span>
          {f.dictionary_name && <span className="dqc-fx-tag">dictionary: {f.dictionary_name}</span>}
        </KV>
        <KV k="What it is">{asText(f.short_description) || "—"}</KV>
        {f.long_description && <KV k="Detail">{asText(f.long_description)}</KV>}
        {(f.prior_display_name || f.prior_short_description) && (
          <KV k="Replaced">
            {f.prior_display_name && <div className="dqc-fx-mut">{asText(f.prior_display_name)}</div>}
            {f.prior_short_description && <div className="dqc-fx-mut">{asText(f.prior_short_description)}</div>}
            <div className="dqc-fx-hint">what the client's dictionary overrode</div>
          </KV>
        )}

        <div className="dqc-fx-sh">Shape</div>
        <KV k="Physical type">{f.data_type || "—"}</KV>
        <KV k="Profile">
          {fmt(f.record_count)} rows · {fmt(f.distinct_count)} distinct · {pct(f.nullable_pct)} empty
          {f.uniqueness_ratio != null && <> · uniqueness {Number(f.uniqueness_ratio).toFixed(4)}</>}
        </KV>
        <KV k="Role in the data">
          {f.semantic_role || "—"}
          {roles.length > 1 && <span className="dqc-fx-mut"> (+{roles.length - 1} alternative)</span>}
          {roles.length > 0 && (
            <div className="dqc-fx-roles">
              {roles.map((r: any, i: number) => (
                <div key={i}>
                  <b>{r?.role}</b> · confidence {r?.confidence}
                  {r?.confirmation_required && <span className="dqc-fx-tag warn">needs confirmation</span>}
                  {false && asList(r?.signals).length > 0 && (   /* internal scoring signals - not for the SME */
                    <div className="dqc-fx-sig">
                      {asList(r?.signals).map((s: any) => `${s?.name}=${asText(s?.value)} (weight ${s?.weight})`).join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </KV>

        <div className="dqc-fx-sh">Governed values</div>
        {doms.length === 0 ? (
          <Non>
            No governed value set is bound to this field
            {domIds.length > 0 && <> (the record names {domIds.join(", ")}, but no matching domain resolved)</>}.
            An allowed-values rule here would enumerate whatever the data happens to contain, not policy.
          </Non>
        ) : doms.map((d: any, i: number) => (
          <div className="dqc-fx-dom" key={d?.domain_id || i}>
            <div className="dqc-fx-dom-t">{d?.name || d?.domain_id}</div>
            <div className="dqc-fx-tags">
              {d?.authority && <span className={"dqc-fx-tag " + (d.authority === "client_confirmed" ? "ok" : "")}>{d.authority}</span>}
              {d?.status && <span className="dqc-fx-tag">{d.status}</span>}
              <span className="dqc-fx-tag info">{asList(d?.values).length} governed codes</span>
              {d?.promoted_value_count ? <span className="dqc-fx-tag">{d.promoted_value_count} promoted</span> : null}
            </div>
            <div className="dqc-fx-mono">
              {d?.domain_id}{d?.source_system ? ` · ${d.source_system}` : ""}
              {d?.source_ref?.entity_id ? ` · ${d.source_ref.entity_id}` : ""}
            </div>
            <div className="dqc-fx-hint">The codes themselves, and how they compare with the data, are on Values.</div>
          </div>
        ))}

        {linked.length > 0 && (
          <>
            <div className="dqc-fx-sh">Related fields</div>
            <KV k="Linked">
              {linked.map(asText).join(", ")}
              <div className="dqc-fx-hint">declared by the client — the strongest candidates for a cross-field rule</div>
            </KV>
          </>
        )}

        {(f.business_context || f.business_responsibility || f.sap_view) && (
          <>
            <div className="dqc-fx-sh">Business</div>
            {f.business_context && <KV k="Context">{asText(f.business_context)}</KV>}
            {f.business_responsibility && <KV k="Responsibility">{asText(f.business_responsibility)}</KV>}
            {f.sap_view && <KV k="SAP view">{asText(f.sap_view)}</KV>}
          </>
        )}

        {(f.known_gaps || gs.length > 0) && (
          <>
            <div className="dqc-fx-sh">Known gaps</div>
            {/* known_gaps is a free-text paragraph on some fields and a list on others, so it
                goes through asText rather than being assumed either way. Getting this wrong is
                what crashed this panel on the 16 fields that carry it. */}
            {f.known_gaps && (
              <div className="dqc-fx-gap">
                {asText(f.known_gaps)}
                <div className="dqc-fx-hint">declared by the client — rules here need care</div>
              </div>
            )}
            {gs.length > 0 && <GapList gaps={gs} />}
          </>
        )}

        {ev.length > 0 && (
          <>
            <div className="dqc-fx-sh">Evidence ({ev.length})</div>
            {ev.map((e: any, i: number) => (
              <div className="dqc-fx-ev" key={e?.evidence_id || i}>
                <div className="dqc-fx-tags">
                  <span className={"dqc-fx-tag " + (e?.authority === "observed" ? "info" : "")}>
                    {e?.kind || e?.evidence_type} · {e?.authority}
                  </span>
                  {e?.confidence != null && <span className="dqc-fx-tag">confidence {e.confidence}</span>}
                </div>
                <div>{asText(e?.statement)}</div>
                {e?.excerpt && <div className="dqc-fx-excerpt">{asText(e.excerpt)}</div>}
                <div className="dqc-fx-mono">{asText(e?.source_id)}</div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

/** Artifact-level knowledge gaps naming this field. `recommended_question` is literally what
 *  the SME is being asked to resolve, and until now it was not reachable from any screen. */
const GapList: React.FC<{ gaps: any[] }> = ({ gaps }) => (
  <>
    {gaps.map((g: any, i: number) => (
      <div className="dqc-fx-gap" key={g?.gap_id || i}>
        <div className="dqc-fx-tags">
          <span className={"dqc-fx-tag " + (g?.severity === "high" ? "bad" : g?.severity === "medium" ? "warn" : "")}>
            {GAP_LABEL[String(g?.gap_type)] || String(g?.gap_type || "").replace(/_/g, " ")} · {g?.severity}
          </span>
        </div>
        <div>{plainGap(asText(g?.description))}</div>
        {g?.recommended_question && <div className="dqc-fx-q">{asText(g.recommended_question)}</div>}
      </div>
    ))}
  </>
);

/* ───────────────────────── controls panel (BC) ───────────────────────── */
// Controls that name this field as owner or participant. The reviewer's question is almost
// always "should this become a rule, and if not, why not" — so each card leads with
// eligibility, the exclusion reason, and the question the pipeline has parked for her, then
// the logic a rule would be built from, then the blockers, with the audit trail one click
// away rather than crowding the decision.
const ControlsPanel: React.FC<{ controls: any[]; fieldId: string; trace?: any }> = ({ controls, fieldId, trace }) => {
  const all = asList(controls);
  const [kind, setKind] = useState<string>("all");
  const [elig, setElig] = useState<"all" | "yes" | "no">("all");
  if (!all.length) {
    return <Non>No business control references {fieldId}. Any rule on this field was derived from the data, not from your control library.</Non>;
  }
  const kinds = ["all"].concat(Array.from(new Set(all.map((c) => String(c?.control_kind || "—")))));
  const shown = all
    .filter((c) => kind === "all" || String(c?.control_kind || "—") === kind)
    .filter((c) => elig === "all" || (elig === "yes" ? !!c?.rule_generation_eligible : !c?.rule_generation_eligible));
  const owned = all.filter((c) => up(c?.owner_field_id) === up(fieldId)).length;
  const yes = all.filter((c) => c?.rule_generation_eligible).length;
  const asking = all.filter((c) => c?.sme_question).length;
  return (
    <div>
      <div className="dqc-sub">
        {all.length} control{all.length === 1 ? "" : "s"} reference this field · {owned} own it ·
        {" "}{yes} can become a rule{asking > 0 ? ` · ${asking} waiting on your answer` : ""}
      </div>
      <div className="dqc-fx-kinds">
        {kinds.map((k) => (
          <button key={k} className={"dqc-fx-kchip" + (kind === k ? " on" : "")} onClick={() => setKind(k)}>{k}</button>
        ))}
        <span className="dqc-fx-kspacer" />
        {([["all", "all"], ["yes", "can become a rule"], ["no", "cannot"]] as const).map(([v, label]) => (
          <button key={v} className={"dqc-fx-kchip" + (elig === v ? " on" : "")} onClick={() => setElig(v)}>{label}</button>
        ))}
      </div>

      {shown.length === 0 && <Non>No control matches these filters.</Non>}

      {shown.map((c: any, idx: number) => {
        const ok = !!c?.rule_generation_eligible;
        const spec = c?.execution_specification || {};
        const ra = c?.readiness_assessment || {};
        const qd = c?.quality_dimensions || {};
        const der = c?.derivation || {};
        const pfs = asList(c?.participating_fields);
        const issues = asList(c?.challenge_issues);
        const blocking = asList(ra?.blocking_reasons);
        const logic = c?.predicate_template || spec?.logical_expression || c?.business_expression;
        return (
          <div className="dqc-fx-ctrl" key={c?.control_id || idx}>
            <div className="dqc-fx-tags">
              <span className={"dqc-fx-tag " + (ok ? "ok" : "bad")}>{ok ? "can become a rule" : "cannot become a rule"}</span>
              {c?.control_kind && <span className="dqc-fx-tag">{c.control_kind}</span>}
              {c?.family && <span className="dqc-fx-tag">{c.family}</span>}
              {c?.category && <span className="dqc-fx-tag">{c.category}</span>}
              {c?.origin && <span className="dqc-fx-tag info">{c.origin}</span>}
              {c?.authority && <span className={"dqc-fx-tag " + (c.authority === "client_confirmed" ? "ok" : "")}>{c.authority}</span>}
              {c?.lane_name && <span className="dqc-fx-tag warn">lane {c?.lane != null ? `${c.lane} · ` : ""}{c.lane_name}</span>}
              {c?.readiness && <span className="dqc-fx-tag">{c.readiness}</span>}
              {c?.constraint_family && <span className="dqc-fx-tag">{c.constraint_family}</span>}
              {c?.execution_ready && <span className="dqc-fx-tag ok">execution ready</span>}
              {c?.challenge_required && <span className="dqc-fx-tag warn">challenge required</span>}
              {c?.confidence != null && <span className="dqc-fx-tag">confidence {c.confidence}</span>}
            </div>

            <div className="dqc-fx-ctrl-t">{asText(c?.title)}</div>

            {/* The question the pipeline parked for the reviewer. It is the point of the card. */}
            {c?.sme_question && (
              <div className="dqc-fx-q ask">
                {asText(c.sme_question)}
                {c?.lane_needs && <div className="dqc-fx-hint">needs: {asText(c.lane_needs)} · {asText(c.lane_state)}</div>}
              </div>
            )}

            {c?.business_statement && <div className="dqc-fx-ctrl-s">{asText(c.business_statement)}</div>}
            {!ok && c?.rule_generation_exclusion_reason && (
              <div className="dqc-fx-whynot">Excluded: {asText(c.rule_generation_exclusion_reason)}</div>
            )}

            {c?.business_intent && <KV k="Intent">{asText(c.business_intent)}</KV>}
            {c?.condition && <KV k="When">{asText(c.condition)}</KV>}
            {c?.expected_outcome && <KV k="Then">{asText(c.expected_outcome)}</KV>}
            {c?.business_reason && <KV k="Why">{asText(c.business_reason)}</KV>}
            {c?.condition_code && (
              <KV k="Condition code">
                <code className="dqc-fx-mono">{asText(c.condition_code)}</code>
                {c?.condition_code_meaning && <div className="dqc-fx-hint">{asText(c.condition_code_meaning)}</div>}
              </KV>
            )}
            {logic && <KV k="Logic"><code className="dqc-fx-mono">{asText(logic)}</code></KV>}

            {pfs.length > 0 && (
              <KV k="Fields involved">
                <div className="dqc-fx-pf">
                  {pfs.map((p: any, i: number) => {
                    const pid = typeof p === "string" ? p : p?.field_id;
                    const isThis = up(pid) === up(fieldId);
                    return (
                      <div key={pid || i} className={isThis ? "on" : ""}>
                        <span className="id">{asText(pid)}</span>
                        {typeof p !== "string" && p?.role && <span className="role">{p.role}</span>}
                        {typeof p !== "string" && p?.confidence != null && <span className="cf">{p.confidence}</span>}
                      </div>
                    );
                  })}
                </div>
              </KV>
            )}
            {asList(c?.grain_fields).length > 0 && <KV k="Grain">{asList(c.grain_fields).map(asText).join(", ")}</KV>}
            {asList(c?.target_tables).length > 0 && (
              <KV k="Tables">
                {asList(c.target_tables).map(asText).join(", ")}
                {asList(c?.target_entities).length > 0 && <span className="dqc-fx-mut"> · {asList(c.target_entities).map(asText).join(", ")}</span>}
              </KV>
            )}
            {asList(c?.join_conditions).length > 0 && (
              <KV k="Join">
                {asList(c.join_conditions).map((j: any, i: number) => (
                  <div key={i} className="dqc-fx-mono">
                    {asText(j?.left_field_id)} {asText(j?.operator)} {asText(j?.right_field_id)}
                    {j?.join_type ? ` (${j.join_type})` : ""}
                  </div>
                ))}
              </KV>
            )}
            {c?.requires_reference_data && (
              <KV k="Reference data">
                {asList(c?.reference_data).length === 0
                  ? <span className="dqc-fx-bad">required, but none is bound — this is why the control cannot run</span>
                  : asList(c.reference_data).map((r: any, i: number) => (
                    <div key={i}>
                      <span className="dqc-fx-mono">{asText(r?.reference_domain_id || r?.reference_set_id)}</span>
                      {r?.value_count != null && <span className="dqc-fx-mut"> · {r.value_count} values</span>}
                      {r?.governance_status && <span className="dqc-fx-mut"> · {r.governance_status}</span>}
                      {r?.description && <div className="dqc-fx-hint">{asText(r.description)}</div>}
                    </div>
                  ))}
              </KV>
            )}

            {/* Why it is not executable, in the reviewer's language. */}
            {(issues.length > 0 || blocking.length > 0) && (
              <div className="dqc-fx-issuebox">
                {blocking.length > 0 && <div className="dqc-fx-mono">blocked by: {blocking.map(asText).join(", ")}</div>}
                {issues.map((i2: any, i: number) => (
                  <div className="dqc-fx-issue" key={i}>
                    <span className={"dqc-fx-tag " + (i2?.severity === "error" ? "bad" : "warn")}>
                      {asText(i2?.issue_type)}{i2?.blocking ? " · blocking" : ""}
                    </span>
                    <div>{asText(i2?.message)}</div>
                    {i2?.recommendation && <div className="dqc-fx-hint">{asText(i2.recommendation)}</div>}
                  </div>
                ))}
              </div>
            )}
            {asList(c?.quality_issues).length > 0 && (
              <div className="dqc-fx-issues">{asList(c.quality_issues).map((q: any) => asText(q?.issue || q)).join(" · ")}</div>
            )}

            {c?.overall_readiness_score != null && (
              <div className="dqc-fx-scores">
                readiness {c.overall_readiness_score}
                {" · "}structure {qd?.structure ?? c?.structure_score ?? "—"}
                {" · "}evidence {qd?.evidence ?? c?.evidence_score ?? "—"}
                {" · "}semantic {qd?.semantic_confidence ?? c?.semantic_confidence ?? "—"}
                {" · "}config {qd?.configuration ?? c?.configuration_score ?? "—"}
                {" · "}simulation {qd?.simulation ?? c?.simulation_score ?? "—"}
                {" · "}approval {qd?.approval ?? c?.approval_score ?? "—"}
                {c?.quality_score != null && <> {" · "}quality {c.quality_score}</>}
              </div>
            )}

            <details className="dqc-fx-det">
              <summary>How this control was derived, and its evidence</summary>
              {asList(der?.methods).length > 0 && <KV k="Method">{asList(der.methods).map(asText).join(", ")}</KV>}
              {der?.authority && <KV k="Authority">{asText(der.authority)}{der?.source_confidence != null && ` · ${der.source_confidence}`}</KV>}
              {asList(der?.rationale).length > 0 && <KV k="Rationale">{asList(der.rationale).map(asText).join(" ")}</KV>}
              {asList(c?.inferred_details).length > 0 && (
                <KV k="Inferred">
                  {asList(c.inferred_details).map((d: any, i: number) => (
                    <div key={i}>{asText(d?.statement)}<span className="dqc-fx-mut"> ({d?.field} · {d?.confidence})</span></div>
                  ))}
                </KV>
              )}
              {asList(c?.business_impact).length > 0 && <KV k="Impact">{asList(c.business_impact).map(asText).join(" · ")}</KV>}
              {asList(c?.exceptions).length > 0 && (
                <KV k="Exceptions">{asList(c.exceptions).map((e: any, i: number) => <div key={i}>{asText(e?.description || e)}</div>)}</KV>
              )}
              {c?.applicability?.description && <KV k="Applies to">{asText(c.applicability.description)}</KV>}
              {asList(c?.evidence).length > 0 && asList(c.evidence).map((e: any, i: number) => (
                <div className="dqc-fx-ev" key={e?.evidence_id || i}>
                  <div className="dqc-fx-tags">
                    <span className="dqc-fx-tag info">{asText(e?.evidence_type)} · {asText(e?.authority)}</span>
                    {e?.confidence != null && <span className="dqc-fx-tag">confidence {e.confidence}</span>}
                  </div>
                  <div>{asText(e?.statement)}</div>
                  {e?.raw_excerpt && <div className="dqc-fx-excerpt">{asText(e.raw_excerpt)}</div>}
                  {e?.source?.file_name && <div className="dqc-fx-mono">{asText(e.source.file_name)}{e?.source?.page ? ` p.${e.source.page}` : ""}</div>}
                </div>
              ))}
              {asList(c?.parameters).length > 0 && (
                <KV k="Parameters">
                  {asList(c.parameters).map((pm: any, i: number) => (
                    <div key={i}>
                      <span className="dqc-fx-mono">{asText(pm?.name)}</span>
                      {" = "}{pm?.value == null ? <span className="dqc-fx-bad">not set{pm?.required ? " (required)" : ""}</span> : asText(pm.value)}
                      {asList(pm?.suggested_values).length > 0 &&
                        <span className="dqc-fx-mut"> · suggested {asList(pm.suggested_values).map(asText).join(", ")}</span>}
                    </div>
                  ))}
                </KV>
              )}
              {asList(c?.knowledge_links?.field_ids).length > 0 && (
                <KV k="Knowledge links">{asList(c.knowledge_links.field_ids).map(asText).join(", ")}</KV>
              )}
              {c?.provenance && (
                <KV k="Provenance">
                  <span className="dqc-fx-mono">{asText(c.provenance?.source)}</span>
                  {c.provenance?.object_group && <div className="dqc-fx-mono">{asText(c.provenance.object_group)}</div>}
                </KV>
              )}
              {asList(c?.clause_ids).length > 0 && <KV k="Clauses">{asList(c.clause_ids).map(asText).join(", ")}</KV>}
              {asList(c?.tags).length > 0 && <KV k="Tags">{asList(c.tags).map(asText).join(" · ")}</KV>}
              {c?.mapping_status && <KV k="Mapping">{asText(c.mapping_status)}</KV>}
              {c?.disposition && <KV k="Disposition">{asText(c.disposition)}</KV>}
              {c?.action_type && <KV k="Action">{asText(c.action_type)}{c?.execution_scope ? ` · ${c.execution_scope}` : ""}</KV>}
            </details>

            {/* F2.6 — which rules THIS control produced, and their state. Answers the
                question the whole review kept raising: "eligible, so why no rule?" */}
            {(() => {
              const tc = trace?.controls?.[String(c?.control_id)];
              const rules = tc?.rules || [];
              const lc = (x: string) => String(x || "").toLowerCase();
              const cls = (x: string) => {
                const l = lc(x);
                return l === "approved" || l === "active" ? "ok"
                  : l === "rejected" ? "bad" : l === "pending" ? "warn" : "info";
              };
              if (rules.length) {
                return (
                  <div className="dqc-fx-trace">
                    <span className="dqc-fx-trace-h">produced {rules.length} rule{rules.length === 1 ? "" : "s"}:</span>
                    {rules.map((r: any) => (
                      <span key={r.rule_id} className={"dqc-fx-tag " + cls(r.lifecycle)}
                        title={r.rule_id}>{r.rule_id.length > 34 ? r.rule_id.slice(0, 32) + "…" : r.rule_id} · {lc(r.lifecycle) || "unreviewed"}</span>
                    ))}
                  </div>
                );
              }
              return (
                <div className="dqc-fx-trace">
                  <span className={"dqc-fx-tag " + (c?.rule_generation_eligible ? "warn" : "")}>
                    {c?.rule_generation_eligible
                      ? "eligible, but produced no rule"
                      : "not eligible — no rule expected"}
                  </span>
                </div>
              );
            })()}
            <div className="dqc-fx-mono dqc-fx-ctrl-id">{asText(c?.control_id)}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ───────────────────────────── main ───────────────────────────── */
// Prop contract is v1's, unchanged — this stays a drop-in swap at the call site.
//   <SdhReviewWorkspace_v2 appNm={appNm} entityId={selectedEntityId} entityLabel={currentShell?.label} />
// `appNm` is required because sdh_exportRulebook / sdh_applyDecisions / sdh_preview all take
// it, and the cockpit hosted here uses all three.
type Props = { entityId: string; entityLabel?: string; appNm: string };
type TabKey = "review" | "dashboard" | "technical" | "policies";
type ModalKind = "values" | "knowledge" | "controls";

const SdhReviewWorkspace_v2: React.FC<Props> = ({ entityId, entityLabel, appNm }) => {
  /* ── host plumbing: the modal stack v1's default export owns ────────────── */
  const [tab, setTab] = usePersisted<TabKey>(`dq2:${entityId}:tab`, "review");
  const [flash, setFlash] = useState<string | null>(null);
  const [detail, setDetail] = useState<EnrichedRule | null>(null);
  const [importing, setImporting] = useState(false);
  const [addingRule, setAddingRule] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [studioFor, setStudioFor] = useState<EnrichedRule | null>(null);
  const [versionsFor, setVersionsFor] = useState<EnrichedRule | null>(null);
  const [bucket, setBucket] = useState<Bucket>(null);
  const polDoc = usePolicies(entityId);          // policies & controls (DQ_POLICIES), replacing the BC controls
  // a status picked on a TABLE or FIELD card filters only that table / field - the queue on top stays as it is
  const [local, setLocal] = useState<{ scope: "table" | "field"; key: string; b: Bucket } | null>(null);    // SME action queue -> filters the rule list (all fields)
  const [valuesFor, setValuesFor] = useState<{ table: string; column: string; codes?: string[] } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // set-aside rules, each with ONE actionable category (read-only) - for the queue's "Set aside" tile
  const [saSummary, setSaSummary] = useState<any>(undefined);
  const [saCat, setSaCatState] = useState<string | null>(null);
  const setSaCat = (c: string | null) => { SA_STORE.cat = c; setSaCatState(c); };   // the rule list reads it too
  useEffect(() => { let live = true; sdh_setaside(entityId).then((r: any) => { if (live) { SA_STORE.rules = r?.rules || {}; SA_STORE.summary = r?.summary || {}; setSaSummary(r?.summary); } }).catch(() => {});
    return () => { live = false; }; }, [entityId, reloadKey]); // eslint-disable-line
  const [assessDefs, setAssessDefs] = useState<AssessmentDef[]>(SME_ASSESSMENTS);
  useEffect(() => { sdh_smeTaxonomy().then(setAssessDefs).catch(() => { /* fall back to the built-in list */ }); }, []);
  const note = useCallback((m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2600); }, []);

  const openDetail = useCallback(async (ruleId: string) => {
    try { const r = await sdh_ruleDetail(entityId, ruleId); setDetail(r.rule); }
    catch { note("Could not load that rule."); }
  }, [entityId, note]);

  /* ── field-centric state ────────────────────────────────────────────────── */
  const [rules, setRules] = useState<EnrichedRule[]>([]);
  const [tables, setTables] = useState<ProfileTable[]>([]);
  const [columns, setColumns] = useState<ProfileColumn[]>([]);
  const [scope, setScope] = useState<ScopeInfo | null>(null);
  const [trace, setTrace] = useState<Traceability | null>(null);
  const [table, setTable] = usePersisted<string>(`dq2:${entityId}:table`, "");
  const [field, setField] = usePersisted<string>(`dq2:${entityId}:field`, "");
  const [inScopeOnly, setInScopeOnly] = usePersisted<boolean>(`dq2:${entityId}:inScopeOnly`, true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [modal, setModal] = useState<{ kind: ModalKind; fieldId: string } | null>(null);
  // Per-table card facts, fetched once per table. Per-field deep context, fetched on demand.
  const [summary, setSummary] = useState<Record<string, Record<string, FieldSummary>>>({});
  const [ctx, setCtx] = useState<Record<string, FieldContext>>({});
  const [ctxBusy, setCtxBusy] = useState(false);
  // The facet rail is controlled from here, so the trigger can be moved or duplicated later
  // without touching the cockpit.
  const [railOpen, setRailOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  useEffect(() => {
    let live = true;
    setBusy(true);
    Promise.all([
      sdh_rules(entityId, "All").catch(() => ({ rules: [] } as any)),
      sdh_dataProfile(entityId).catch(() => ({ tables: [], columns: [] } as any)),
      sdh_scope(entityId).catch(() => null),
      sdh_traceability(entityId).catch(() => null),
    ]).then(([rr, pp, sc, tr]: any) => {
      if (!live) return;
      setRules(rr?.rules || []);
      setTables(pp?.tables || []);
      setColumns(pp?.columns || []);
      setScope(sc || null);
      setTrace(tr || null);
      const first = (pp?.tables || [])[0];
      if (first) {
        setTable((t) => (t && (pp.tables || []).some((x: any) => up(x.table) === up(t)))
          ? up(t) : up(first.table));
      }
    }).finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* One request per table gives every card its business name, role and source counts. Without
     it only the SELECTED field resolved and every other card read "unknown". */
  useEffect(() => {
    if (!table || summary[table]) return;
    let live = true;
    sdh_fieldContextSummary(entityId, table)
      .then((r) => { if (live) setSummary((p) => ({ ...p, [table]: r.fields || {} })); })
      .catch(() => { if (live) setSummary((p) => ({ ...p, [table]: {} })); });
    return () => { live = false; };
  }, [entityId, table]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Deep context (evidence, roles, controls) only for the field being looked at. */
  useEffect(() => {
    if (!field || ctx[field]) return;
    const parts = field.split("::");
    if (parts.length !== 2) return;
    let live = true;
    setCtxBusy(true);
    sdh_fieldContext(entityId, parts[0], parts[1])
      .then((r) => { if (live) setCtx((p) => ({ ...p, [field]: r })); })
      .catch(() => { /* an empty panel reads as "nothing known", which is the honest state */ })
      .finally(() => { if (live) setCtxBusy(false); });
    return () => { live = false; };
  }, [entityId, field]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── derived ────────────────────────────────────────────────────────────── */
  // the status chosen in the queue filters the WHOLE page: tables, fields, rules ("All" = no filter)
  const bucketRules = useMemo(() => (bucket ? rules.filter((r) => matchBucket(r, bucket)
    && (bucket !== "hidden" || !saCat || saOf(ruleKey(r))?.category === saCat)) : rules),
    [rules, bucket, saCat, saSummary]); // eslint-disable-line
  const rulesByField = useMemo(() => {
    const m: Record<string, EnrichedRule[]> = {};
    bucketRules.forEach((r) => {
      const o = up(r.owner_field || (r as any)._raw?.owner_field_id);
      if (o) (m[o] = m[o] || []).push(r);
    });
    return m;
  }, [bucketRules]);

  const profByField = useMemo(() => {
    const m: Record<string, ProfileColumn> = {};
    columns.forEach((c: any) => { m[fid(c.table || c.source_prefix, c.column || c.orig_column_name)] = c; });
    return m;
  }, [columns]);

  /** The configured scope, uppercased. `null` fields means full_model — everything is in
   *  scope, so the toggle has nothing to narrow and is hidden. */
  const scopeSet = useMemo(() => {
    if (!scope || !scope.fields) return null;
    const s = new Set<string>();
    scope.fields.forEach((f) => s.add(up(f)));
    return s;
  }, [scope]);

  /** Scope entries that name no profiled column, for THIS table. Shown as ghost cards rather
   *  than dropped: AUSP::FP-BOM-OWNER is in the client's agreed 25 and simply is not in the
   *  extract, and "why does this field never appear" is a question worth answering on screen. */
  const absentHere = useMemo(() => {
    if (!scope) return [] as string[];
    return (scope.unresolved || [])
      .map(up)
      .filter((f) => f.indexOf("::") > 0 && f.split("::")[0] === table)
      .filter((f) => !profByField[f]);
  }, [scope, table, profByField]);

  type Card = { fieldId: string; column: string; absent?: boolean };
  const fields = useMemo(() => {
    const seen = new Map<string, Card>();
    columns.forEach((c: any) => {
      const t = up(c.table || c.source_prefix);
      if (t !== table) return;
      const col = String(c.column || c.orig_column_name || "");
      if (!col) return;
      seen.set(fid(t, col), { fieldId: fid(t, col), column: col });
    });
    // A field that owns a rule must always be reachable, even if profiling missed it —
    // otherwise its rules are unreviewable from this screen.
    Object.keys(rulesByField).forEach((f) => {
      if (f.indexOf(table + "::") === 0 && !seen.has(f)) seen.set(f, { fieldId: f, column: f.split("::")[1] });
    });
    absentHere.forEach((f) => {
      if (!seen.has(f)) seen.set(f, { fieldId: f, column: f.split("::")[1], absent: true });
    });

    let list = Array.from(seen.values());
    if (inScopeOnly && scopeSet) {
      const inScope = list.filter((f) => scopeSet.has(f.fieldId));
      // Some tables hold none of the configured fields. Showing an empty sidebar there reads
      // as a broken screen, so fall back to the fields that own rules.
      list = inScope.length ? inScope : list.filter((f) => (rulesByField[f.fieldId] || []).length > 0);
    }
    if (bucket) list = list.filter((f) => (rulesByField[f.fieldId] || []).length > 0);   // only fields with rules in that status
    else if (local && local.scope === "table" && local.key === table)
      list = list.filter((f) => (rulesByField[f.fieldId] || []).some((r) => matchBucket(r, local.b)));
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((f) =>
        `${f.fieldId} ${summary[table]?.[f.fieldId]?.display_name || ""}`.toLowerCase().indexOf(needle) >= 0);
    }
    return list.sort((a, b) => a.column.localeCompare(b.column));
  }, [columns, table, inScopeOnly, scopeSet, q, rulesByField, summary, absentHere, bucket, local]);

  // Keep the selection valid: if the persisted field is not in the current list, take the
  // first one rather than showing an empty pane against a field the reviewer cannot see.
  useEffect(() => {
    if (!fields.length) { if (field) setField(""); return; }
    if (!fields.some((f) => f.fieldId === field)) setField(fields[0].fieldId);
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableCounts = useMemo(() => {
    const m: Record<string, { fields: number; rules: number }> = {};
    columns.forEach((c: any) => {
      const t = up(c.table || c.source_prefix);
      if (!t) return;
      (m[t] = m[t] || { fields: 0, rules: 0 }).fields += 1;
    });
    bucketRules.forEach((r) => {
      const t = up(r.owner_field || (r as any)._raw?.owner_field_id).split("::")[0];
      if (t) (m[t] = m[t] || { fields: 0, rules: 0 }).rules += 1;
    });
    return m;
  }, [columns, rules, bucketRules]);
  // a status with no rule in the current table moves to the first table that has some
  useEffect(() => {
    if (!bucket) return;
    if ((tableCounts[table] || { rules: 0 }).rules > 0) return;
    const t = Object.keys(tableCounts).find((k) => tableCounts[k].rules > 0);
    if (t && t !== table) setTable(t);
  }, [bucket, tableCounts]); // eslint-disable-line

  const selSum = summary[table]?.[field];
  const selCtx = ctx[field];
  const selAbsent = absentHere.indexOf(field) >= 0;

  const doExport = useCallback(async () => {
    try { note("Building workbook…"); await sdh_exportRulebook(entityId, appNm); note("Downloaded."); }
    catch { note("Export failed."); }
  }, [entityId, appNm, note]);

  const openModal = (kind: ModalKind, fieldId: string) => { setField(fieldId); setModal({ kind, fieldId }); };
  const modalParts = modal ? modal.fieldId.split("::") : ["", ""];
  const modalSum = modal ? summary[table]?.[modal.fieldId] : undefined;
  const modalCtx = modal ? ctx[modal.fieldId] : undefined;

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <HelpProvider>
      <div className="dqc">
        <style>{CSS}</style>
        <style>{CSS_V2}</style>
        <style>{ROLE_CSS + THIN_SCROLL}</style>

        <div className="dqc-top">
          <div>
            <div className="dqc-title">Review rules — {entityLabel || "Product master data"}</div>
            <div className="dqc-sub">
              Pick a field, then approve the rules that reflect your business logic. Each field
              carries its own values, knowledge and controls, so the evidence behind a rule is
              on the same screen as the decision.
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button className="dqc-btn out" onClick={doExport}>⬇ Export rulebook</button>
          <button className="dqc-btn out" onClick={() => setImporting(true)}>⬆ Import reviewed file</button>
          <button className="dqc-btn out" onClick={() => setShowGuide(true)} title="The gates behind Pre-approved / Needs a decision / Set aside">ⓘ How statuses are decided</button>
          <button className="dqc-btn out" style={{ borderColor: "#A100FF", color: "#7500C0" }}
            onClick={() => setAddingRule(true)}>+ Add a rule</button>
        </div>

        <div className="dqc-tabs">
          <button className={"dqc-tab" + (tab === "dashboard" || tab === "technical" ? " on" : "")} onClick={() => setTab("dashboard")}>Summary</button>
          <button className={"dqc-tab" + (tab === "review" ? " on" : "")} onClick={() => setTab("review")}>Review — QA / SME</button>
          <button className={"dqc-tab" + (tab === "policies" ? " on" : "")} onClick={() => setTab("policies")}>Policies &amp; Controls</button>
        </div>

        <div className="dqc-body">
          {tab === "dashboard" && <><div className="dqc-techlink"><button onClick={() => setTab("technical")}>Technical view →</button></div>
            <LeadershipView rules={rules} onOpen={(r) => openDetail(r.rule_id)} /></>}
          {tab === "technical" && <><div className="dqc-techlink"><button onClick={() => setTab("dashboard")}>← Summary</button></div>
            <TechnicalView rules={rules} onOpen={(r) => openDetail(r.rule_id)} /></>}
          {tab === "policies" && <PoliciesView entityId={entityId} appNm={appNm} onOpen={(id) => openDetail(id)} />}

          {tab === "review" && (
            <div className="dqc-fx">
              {/* F2.5 — entity-wide lifecycle roll-up from the traceability rule map (lifecycle
                  is the latest-decision-by-decided-at gate). The at-a-glance status the review
                  lacked; the two trailing chips are the traceability headline. */}
              {/* entity-level status from the RULES themselves (the BC trace counted 0 rules and "controls") */}
              <div className="dqc-fx-full"><ActionQueue rules={rules} active={bucket} onPick={(b) => { setBucket(b); setLocal(null); setSaCat(null); }} setAside={saSummary}
                saCat={saCat} onPickCat={(c) => { setSaCat(c); setBucket(c ? "hidden" : bucket); setLocal(null); }} /></div>
              {/* table strip */}
              <div className="dqc-fx-full">
                <div className="dqc-fx-strip">
                  {tables.map((t: any) => {
                    const name = up(t.table || t.name);
                    const c = tableCounts[name] || { fields: 0, rules: 0 };
                    return (
                      <button key={name} className={"dqc-fx-tbtn" + (name === table ? " on" : "") + (bucket && !c.rules ? " dim" : "")}
                        onClick={() => setTable(name)}>
                        <span className="t">{name}</span>
                        <span className="m">{fmt(t.records != null ? t.records : (t as any).rows)} rows · {c.fields} fields · {c.rules} rules</span>
                        {!bucket && <StatusMini rules={rules.filter((r) => up(r.owner_field || (r as any)._raw?.owner_field_id).split("::")[0] === name)}
                          onPick={(b) => { setTable(name); setLocal({ scope: "table", key: name, b }); }} />}
                      </button>
                    );
                  })}
                  {!busy && tables.length === 0 && <span className="dqc-fx-mut">No profiled tables for this entity.</span>}
                </div>
              </div>

              {/* field list — the spine of the screen */}
              <aside className="dqc-fx-side">
                <div className="dqc-fx-search">
                  🔎<input value={q} placeholder="Find a field…" onChange={(e) => setQ(e.target.value)} />
                </div>
                {scopeSet && (
                  <label className="dqc-fx-scope" title={scope?.reason || "The field scope agreed for this entity"}>
                    <input type="checkbox" checked={inScopeOnly} onChange={(e) => setInScopeOnly(e.target.checked)} />
                    <span>Agreed scope only</span>
                    <span className="n">{scopeSet.size}</span>
                  </label>
                )}
                <div className="dqc-fx-scroll">
                  {busy && <div className="dqc-fx-mut dqc-fx-pad">Loading fields…</div>}
                  {!busy && fields.length === 0 && <div className="dqc-fx-mut dqc-fx-pad">No field matches.</div>}
                  {fields.map((f) => {
                    const s = summary[table]?.[f.fieldId];
                    const p = profByField[f.fieldId];
                    const kind = kindOf(s, p);
                    const n = (rulesByField[f.fieldId] || []).length;
                    // Before the summary lands nothing is known, so nothing is disabled —
                    // greying an icon on unknown data would hide a source that does exist.
                    const hasK = s ? s.ekd_present : true;
                    const nControls = s ? s.control_count : -1;
                    return (
                      <div key={f.fieldId} className={"dqc-fx-card" + (f.fieldId === field ? " on" : "") + (f.absent ? " absent" : "")}
                        onClick={() => setField(f.fieldId)}>
                        <div className="dqc-fx-card-h">
                          <div className="dqc-fx-card-n">
                            <div className="nm">{s?.display_name || f.column}</div>
                            <div className="ph">{f.column}</div>
                          </div>
                          <div className="dqc-fx-card-r">
                            <span className={"dqc-fx-kind k-" + kind}>{kind}</span>
                            <div className="rn">{n} rule{n === 1 ? "" : "s"}</div>
                          </div>
                        </div>
                        {!bucket && <StatusMini rules={rulesByField[f.fieldId] || []} onPick={(b) => { setField(f.fieldId); setLocal({ scope: "field", key: f.fieldId, b }); }} />}
                        {f.absent ? (
                          <div className="dqc-fx-absent">In the agreed scope, but not delivered in this extract.</div>
                        ) : p ? (
                          <div className="dqc-fx-card-m">
                            {fmt((p as any).records)} rows · {fmt((p as any).distinct)} distinct · {pct((p as any).null_pct)} empty
                          </div>
                        ) : null}
                        <div className="dqc-fx-btns">
                          <button className="dqc-fx-btn" title="Values in your data, and the governed list they are checked against"
                            onClick={(e) => { e.stopPropagation(); openModal("values", f.fieldId); }}>Values</button>
                          <button className="dqc-fx-btn" disabled={!hasK}
                            title={hasK ? "What the enterprise knowledge artifact records about this field" : "Nothing is recorded about this field"}
                            onClick={(e) => { e.stopPropagation(); openModal("knowledge", f.fieldId); }}>Knowledge</button>
                          {(() => { void nControls;
                            const nPol = policiesOfField(polDoc, new Set((rules.filter((r) => up(r.owner_field || (r as any)._raw?.owner_field_id) === f.fieldId)).map((r) => r.rule_id))).length;
                            return <button className="dqc-fx-btn" disabled={nPol === 0}
                              title={nPol > 0 ? `${nPol} polic${nPol === 1 ? "y covers" : "ies cover"} this field` : "No policy covers this field yet"}
                              onClick={(e) => { e.stopPropagation(); openModal("controls", f.fieldId); }}>
                              Policies{nPol > 0 ? ` ${nPol}` : ""}
                            </button>; })()}
                          {((s?.domain_count || 0) > 0 || (s?.declared_domains || 0) > 0) &&
                            <span className="dqc-fx-btn flat" title="A governed value set is bound to this field">governed</span>}
                          {(s?.gap_count || 0) > 0 &&
                            <span className="dqc-fx-btn gap"
                              title={`${s?.gap_count} open knowledge gap(s) — see Knowledge`}>{s?.gap_count} gap{s?.gap_count === 1 ? "" : "s"}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </aside>

              {/* rules — v1's cockpit, scoped to the chosen field */}
              <main className="dqc-fx-main">
                <div className="dqc-fx-head">
                  <div className="dqc-fx-head-n">
                    <div className="nm">{selSum?.display_name || (field ? field.split("::")[1] : "—")}</div>
                    <div className="ph">{field || "no field selected"}</div>
                  </div>
                  <div style={{ flex: 1 }} />
                  {ctxBusy && !selCtx && <span className="dqc-fx-mut">loading field detail…</span>}
                  {selCtx && (
                    <span className="dqc-fx-tags">
                      {selSum?.metadata_authority && <span className={"dqc-fx-tag " + (selSum.metadata_authority === "dictionary_confirmed" ? "ok" : "")}>{selSum.metadata_authority}</span>}
                      {selCtx.counts.domains > 0 && <span className="dqc-fx-tag info">{selCtx.counts.governed_values} governed values</span>}
                      {selCtx.counts.controls > 0 && <span className="dqc-fx-tag">{selCtx.counts.controls} controls</span>}
                      {!selCtx.ekd_present && <span className="dqc-fx-tag warn">no knowledge record</span>}
                    </span>
                  )}
                  <button className={"dqc-toggle" + (activeFilters ? " on" : "")} onClick={() => setRailOpen(true)}
                    title="Narrow the rules below by status, reason, dimension, measurement, trust, risk, table, type or scope">
                    ⚙ Filters{activeFilters ? ` · ${activeFilters}` : ""}
                  </button>
                </div>

                {selAbsent && (
                  <div className="dqc-empty">
                    This field is in the agreed scope but was not delivered in the extract, so nothing
                    was profiled and no rule could be generated against it. That is a data-delivery
                    gap, not a rule gap.
                  </div>
                )}

                {/* The cockpit is ALWAYS mounted, even when the selected field has no rules.
                    It holds the reviewer's unsaved decisions, her scroll position and her
                    preview results; unmounting it to show an empty state would throw all
                    three away the moment she clicked the wrong field. */}
                <ReviewCockpit
                  lifeFilter={bucket || (local && ((local.scope === "field" && local.key === field) || (local.scope === "table" && local.key === table)) ? local.b : null)}
                  onClearLife={() => { setBucket(null); setLocal(null); }} hideGates
                  entityId={entityId}
                  appNm={appNm}
                  onOpen={openDetail}
                  onNote={note}
                  assessDefs={assessDefs}
                  refreshSignal={reloadKey}
                  fieldFilter={field || null}
                  railMode="popover"
                  railOpen={railOpen}
                  onRailOpenChange={setRailOpen}
                  onActiveFilters={setActiveFilters}
                  onRulesLoaded={setRules}
                />
              </main>
            </div>
          )}
        </div>

        {flash && <div className="dqc-flash">{flash}</div>}

        {/* ── per-field panels ── */}
        {modal?.kind === "values" && (
          <FieldValuesPanel entityId={entityId} table={modalParts[0]} column={modalParts[1]}
            onClose={() => setModal(null)} />
        )}
        {modal?.kind === "knowledge" && (
          <Wide title={`What we know${modalSum?.display_name ? ` — ${modalSum.display_name}` : ""}`}
            sub={modal.fieldId} onClose={() => setModal(null)}>
            <PanelBoundary what={`${modal.fieldId} (knowledge)`}>
              {!modalCtx ? <Non>Loading…</Non> : (
                <KnowledgePanel f={modalCtx.ekd}
                  domains={modalCtx.reference_domains}
                  gaps={(modalCtx as any).knowledge_gaps} />
              )}
            </PanelBoundary>
          </Wide>
        )}
        {modal?.kind === "controls" && (
          <Wide title={`Policies & controls${modalSum?.display_name ? ` — ${modalSum.display_name}` : ""}`}
            sub={modal.fieldId} onClose={() => setModal(null)}>
            <PanelBoundary what={`${modal.fieldId} (controls)`}>
              {(() => { void ControlsPanel; void modalCtx;
                const ids = new Set(rules.filter((r) => up(r.owner_field || (r as any)._raw?.owner_field_id) === modal.fieldId).map((r) => r.rule_id));
                return <FieldPolicies items={policiesOfField(polDoc, ids)} onOpenRule={(id) => { setModal(null); openDetail(id); }} />; })()}
            </PanelBoundary>
          </Wide>
        )}

        {/* ── SME adds a rule: preview first, saved only on her choice (opens on the selected field) ── */}
        {showGuide && <StatusGuide entityId={entityId} onClose={() => setShowGuide(false)} />}
        {addingRule && (
          <SmeAddRuleDrawer entityId={entityId} appNm={appNm} initialOwner={field || undefined}
            onClose={() => setAddingRule(false)}
            onSaved={(m) => { setAddingRule(false); note(m); setReloadKey((k) => k + 1); }} />
        )}
        {/* ── v1's modal stack, hosted unchanged ── */}
        {importing && (
          <SmeImportPanel entityId={entityId} appNm={appNm}
            onClose={() => setImporting(false)}
            onApplied={(m) => { setImporting(false); note(m); setReloadKey((k) => k + 1); }} />
        )}
        {versionsFor && (
          <div className="rd2-over"><RuleVersionsPanel entityId={entityId} appNm={appNm}
            constraintId={versionsFor.rule_id} ruleName={versionsFor.rule_name}
            onClose={() => setVersionsFor(null)}
            onChanged={(m) => { note(m); setDetail(null); setVersionsFor(null); setReloadKey((k) => k + 1); }} /></div>
        )}
        {valuesFor && (
          <div className="rd2-over"><FieldValuesPanel entityId={entityId} table={valuesFor.table} column={valuesFor.column}
            ruleCodes={valuesFor.codes} onClose={() => setValuesFor(null)} /></div>
        )}
        {studioFor && (
          <RuleStudioPanel entityId={entityId} appNm={appNm} rule={studioFor}
            onClose={() => setStudioFor(null)}
            onSaved={(m) => {
              const rid = (studioFor as any)?.rule_id;
              setStudioFor(null); note(m); setReloadKey((k) => k + 1);
              // Stay on the rule and refresh it to the new version rather than dumping her
              // back to the list — same behaviour as v1.
              if (rid) openDetail(rid);
            }} />
        )}
        {detail && (
          <RuleDetailModal entityId={entityId} appNm={appNm} rule={detail} defs={assessDefs}
            onRefine={() => setStudioFor(detail)}
            onFieldValues={(t, c, codes) => setValuesFor({ table: t, column: c, codes })}
            onVersions={() => setVersionsFor(detail)}
            onClose={() => setDetail(null)}
            onDone={(m) => { note(m); setReloadKey((k) => k + 1); }} />
        )}
      </div>
    </HelpProvider>
  );
};

export default SdhReviewWorkspace_v2;

/* ───────────────────────────── styles ─────────────────────────────
   Only what v1 has no class for. Everything here uses v1's own tokens (--ink, --line, --teal,
   --muted, --dqc-disp, --dqc-mono), so the two screens stay in step when v1's palette moves. */
const CSS_V2 = `
.dqc-fx{display:grid;grid-template-columns:304px 1fr;gap:20px;align-items:start}
.dqc-fx-full{grid-column:1/-1}

/* table strip */
.dqc-fx-strip{display:flex;gap:8px;flex-wrap:wrap;margin:0 0 4px;align-items:center}
.dqc-fx-tbtn{all:unset;cursor:pointer;min-width:132px;padding:9px 14px;border:1px solid var(--line);
 border-radius:12px;background:var(--card);transition:box-shadow .15s,border-color .15s}
.dqc-fx-tbtn:hover{box-shadow:0 3px 10px rgba(16,36,54,.07)}
.dqc-fx-tbtn.on{border-color:var(--teal);box-shadow:0 0 0 2px color-mix(in srgb,var(--teal) 18%,#fff)}
.dqc-fx-tbtn .t{display:block;font-family:var(--dqc-disp);font-size:13.5px;font-weight:600}
.dqc-fx-tbtn .m{display:block;font-size:11px;color:var(--muted);margin-top:2px;font-family:var(--dqc-mono)}
.dqc-fx-tbtn.on .t{color:var(--teal-d)}

/* field list */
.dqc-fx-side{position:sticky;top:12px;background:var(--card);border:1px solid var(--line);
 border-radius:14px;padding:8px;box-shadow:0 1px 2px rgba(16,36,54,.03)}
.dqc-fx-search{display:flex;align-items:center;gap:8px;border:1px solid var(--line);border-radius:9px;
 padding:7px 10px;font-size:13px}
.dqc-fx-search input{border:0;outline:0;flex:1;font-size:13px;background:transparent;font-family:inherit;min-width:0}
.dqc-fx-scope{display:flex;align-items:center;gap:7px;padding:8px 4px 4px;font-size:12px;color:var(--ink2);cursor:pointer}
.dqc-fx-scope .n{margin-left:auto;font-family:var(--dqc-mono);font-size:11px;color:#a4b0bc}
.dqc-fx-scroll{max-height:calc(100vh - 250px);overflow-y:auto;padding:4px 2px 2px}
.dqc-fx-pad{padding:14px 8px}
.dqc-fx-card{border:1px solid var(--line);border-radius:10px;padding:8px 10px 9px;margin-bottom:6px;
 cursor:pointer;transition:background .12s,border-color .12s}
.dqc-fx-card:hover{background:#f4f7fa}
.dqc-fx-card.on{border-color:var(--teal);background:#f1f8f7}
.dqc-fx-card.absent{border-style:dashed;border-color:#ead9b4;background:#fdfaf3}
.dqc-fx-card-h{display:flex;gap:8px;align-items:flex-start}
.dqc-fx-card-n{min-width:0;flex:1}
.dqc-fx-card-n .nm{font-size:13px;font-weight:600;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dqc-fx-card-n .ph{font-family:var(--dqc-mono);font-size:11px;color:var(--muted);margin-top:1px}
.dqc-fx-card-r{text-align:right;flex-shrink:0}
.dqc-fx-card-r .rn{font-family:var(--dqc-mono);font-size:11px;color:#a4b0bc;margin-top:3px}
.dqc-fx-card-m{font-family:var(--dqc-mono);font-size:10.5px;color:#9aa6b2;margin-top:5px}
.dqc-fx-absent{font-size:11.5px;color:#8a6516;margin-top:5px;line-height:1.45}
.dqc-fx-btns{display:flex;gap:5px;margin-top:7px;flex-wrap:wrap}
.dqc-fx-btn{all:unset;cursor:pointer;font-size:10.5px;padding:2px 9px;border-radius:20px;
 border:1px solid var(--line);color:#5f6e7d;background:#fff}
.dqc-fx-btn:hover{background:#eef3f7;color:var(--ink)}
.dqc-fx-btn[disabled]{opacity:.42;cursor:default;background:#fafbfc}
.dqc-fx-btn[disabled]:hover{background:#fafbfc;color:#5f6e7d}
.dqc-fx-btn.flat{cursor:default;border-color:#9fe1cb;color:#0b5f5e;background:#e0f3f1}
.dqc-fx-btn.gap{cursor:default;border-color:#ead9b4;color:#8a6516;background:#fbf6ea}

/* selected-field header */
.dqc-fx-main{min-width:0}
.dqc-fx-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:0 2px 12px;
 border-bottom:1px solid var(--line);margin-bottom:14px}
.dqc-fx-head-n .nm{font-family:var(--dqc-disp);font-size:16px;font-weight:600}
.dqc-fx-head-n .ph{font-family:var(--dqc-mono);font-size:11.5px;color:var(--muted)}

/* field kind */
.dqc-fx-kind{display:inline-block;font-size:10px;letter-spacing:.04em;padding:1px 7px;border-radius:20px;
 border:1px solid var(--line);color:var(--muted);background:#fff}
.dqc-fx-kind.k-lov{color:#6b3fa0;border-color:#d8cbe9;background:#f6f2fb}
.dqc-fx-kind.k-number{color:#1d6fa5;border-color:#c5dcea;background:#f1f7fb}
.dqc-fx-kind.k-date{color:#1f7a63;border-color:#bfded4;background:#f0f8f5}
.dqc-fx-kind.k-id{color:#b2582f;border-color:#eccdbc;background:#fdf5f0}
.dqc-fx-kind.k-flag{color:#946011;border-color:#ead9b4;background:#fdf9ef}

/* wide modal, built on v1's popover chrome */
.dqc-pop.wide{width:min(940px,96vw);max-height:86vh;display:flex;flex-direction:column}
.dqc-pop.wide .dqc-pop-h{flex-shrink:0}
.dqc-pop-h .sub{font-family:var(--dqc-mono);font-size:11.5px;color:#9db4c9;font-weight:400;margin-left:9px}
.dqc-fx-popb{overflow-y:auto;padding:14px 18px 20px}

/* key/value rows inside the panels */
.dqc-fx-kv{display:flex;gap:12px;padding:4px 0;font-size:13px;align-items:flex-start}
.dqc-fx-kv .k{width:150px;flex-shrink:0;color:var(--muted)}
.dqc-fx-kv .v{flex:1;min-width:0;color:var(--ink);word-break:break-word;line-height:1.5}
.dqc-fx-hint{font-size:11px;color:#9aa6b2;margin-top:2px;line-height:1.45}
.dqc-fx-mut{color:var(--muted)}
.dqc-fx-bad{color:var(--red)}
.dqc-fx-mono{font-family:var(--dqc-mono);font-size:11.5px;color:#8a95a1;word-break:break-all}
.dqc-fx-sh{font-size:10px;letter-spacing:.11em;text-transform:uppercase;color:#9aa6b2;font-weight:700;margin:16px 0 6px}
.dqc-fx-roles{margin-top:5px;font-size:12px;color:var(--ink2)}
.dqc-fx-roles>div{padding:2px 0}
.dqc-fx-sig{margin-left:10px;color:#9aa6b2;font-size:11px}
.dqc-fx-ev{border-top:1px solid #f0f2f6;padding:6px 0;font-size:12.5px;line-height:1.5}

/* tags — one visual language for every chip in these panels */
.dqc-fx-tags{display:flex;gap:5px;flex-wrap:wrap;align-items:center}
.dqc-fx-tag{display:inline-block;font-size:10.5px;padding:1px 8px;border-radius:20px;margin-left:5px;
 border:1px solid var(--line);color:#5f6e7d;background:#fff;white-space:nowrap}
.dqc-fx-tags .dqc-fx-tag{margin-left:0}
.dqc-fx-tag.ok{color:#0b5f5e;border-color:#9fe1cb;background:#e0f3f1}
.dqc-fx-tag.warn{color:#8a6516;border-color:#ead9b4;background:#fbf6ea}
.dqc-fx-tag.bad{color:var(--red);border-color:#f0cdc8;background:var(--red-bg)}
.dqc-fx-tag.info{color:#1d6fa5;border-color:#c5dcea;background:#f1f7fb}

/* F2.5 lifecycle roll-up bar */
.dqc-lifebar{display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:10px 4px 2px;margin-bottom:2px}
.dqc-lifebar-t{font-family:var(--dqc-disp);font-size:13.5px;font-weight:600;margin-right:4px}
.dqc-lifebar-sp{flex:1;min-width:12px}
.dqc-lifechip{font-size:11.5px;padding:2px 10px;border-radius:20px;border:1px solid var(--line);color:#5f6e7d;background:#fff}
.dqc-lifechip b{font-weight:700}
.dqc-lifechip.ok{color:#0b5f5e;border-color:#9fe1cb;background:#e0f3f1}
.dqc-lifechip.bad{color:var(--red);border-color:#f0cdc8;background:var(--red-bg)}
.dqc-lifechip.warn{color:#8a6516;border-color:#ead9b4;background:#fbf6ea}
.dqc-lifechip.info{color:#1d6fa5;border-color:#c5dcea;background:#f1f7fb}
.dqc-lifechip.mut{color:#8a95a1}

/* F2.6 control → rule trace */
.dqc-fx-trace{display:flex;gap:5px;flex-wrap:wrap;align-items:center;margin-top:8px;padding-top:7px;border-top:1px dashed var(--line)}
.dqc-fx-trace-h{font-size:11.5px;color:var(--muted);margin-right:2px}

/* knowledge panel — domains, gaps, evidence excerpts */
.dqc-fx-dom{border:1px solid var(--line);border-radius:10px;padding:10px 12px;margin:0 0 8px}
.dqc-fx-dom-t{font-size:13px;font-weight:600;margin-bottom:5px}
.dqc-fx-gap{border-left:3px solid #d9a441;background:#fdf9ef;border-radius:0 8px 8px 0;
 padding:9px 12px;margin:0 0 8px;font-size:12.5px;line-height:1.55;color:#6f5a1f}
.dqc-fx-q{margin-top:6px;font-size:12.5px;color:var(--ink);line-height:1.5}
.dqc-fx-q::before{content:"“"}
.dqc-fx-q::after{content:"”"}
.dqc-fx-q.ask{border-left:3px solid var(--teal);background:#eff8f7;border-radius:0 8px 8px 0;
 padding:9px 12px;margin:7px 0;color:#0b4a49}
.dqc-fx-excerpt{font-family:var(--dqc-mono);font-size:11.5px;color:#5f6e7d;background:#f6f8fa;
 border-radius:6px;padding:5px 8px;margin:4px 0;white-space:pre-wrap;word-break:break-word}
.dqc-fx-boundary{border:1px solid #f0cdc8;background:var(--red-bg);border-radius:10px;
 padding:12px 14px;font-size:13px;color:#8d3a33;line-height:1.55}
.dqc-fx-boundary .dqc-fx-mono{margin-top:6px;color:#8d3a33}

/* controls panel */
.dqc-fx-kinds{display:flex;gap:6px;flex-wrap:wrap;margin:10px 0 14px}
.dqc-fx-kchip{all:unset;cursor:pointer;font-size:12px;padding:2px 11px;border-radius:20px;
 border:1px solid var(--line);color:var(--ink2);background:#fff}
.dqc-fx-kchip.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.dqc-fx-ctrl{border:1px solid var(--line);border-radius:10px;padding:11px 13px;margin-bottom:10px}
.dqc-fx-ctrl-t{font-size:13px;font-weight:600;margin-top:6px}
.dqc-fx-ctrl-s{font-size:13px;color:var(--ink2);margin:3px 0 5px;line-height:1.5}
.dqc-fx-ctrl-id{margin-top:7px}
.dqc-fx-whynot{font-size:12px;color:var(--red);background:var(--red-bg);border-radius:7px;padding:5px 9px;margin:4px 0}
.dqc-fx-scores{font-size:11.5px;color:var(--muted);font-family:var(--dqc-mono);margin-top:7px}
.dqc-fx-issues{font-size:11.5px;color:#8a6516;margin-top:4px;line-height:1.45}
.dqc-fx-kspacer{flex:1;min-width:10px}
.dqc-fx-pf{display:grid;gap:2px;margin-top:2px}
.dqc-fx-pf>div{display:flex;gap:8px;align-items:baseline;font-size:12.5px;padding:2px 6px;border-radius:6px}
.dqc-fx-pf>div.on{background:#eff8f7}
.dqc-fx-pf .id{font-family:var(--dqc-mono);font-size:11.5px}
.dqc-fx-pf .role{font-size:11px;color:var(--muted)}
.dqc-fx-pf .cf{margin-left:auto;font-family:var(--dqc-mono);font-size:11px;color:#a4b0bc}
.dqc-fx-issuebox{border:1px solid #f0cdc8;background:#fdf4f3;border-radius:9px;padding:9px 11px;margin:8px 0}
.dqc-fx-issue{font-size:12.5px;line-height:1.5;margin-top:5px}
.dqc-fx-issue:first-child{margin-top:0}
.dqc-fx-det{margin-top:9px;border-top:1px solid var(--line);padding-top:7px}
.dqc-fx-det>summary{cursor:pointer;font-size:12px;color:var(--muted);list-style:none;user-select:none}
.dqc-fx-det>summary::-webkit-details-marker{display:none}
.dqc-fx-det>summary::before{content:"▸ ";color:#a4b0bc}
.dqc-fx-det[open]>summary::before{content:"▾ "}
.dqc-fx-det>summary:hover{color:var(--ink)}

@media(max-width:1100px){.dqc-fx{grid-template-columns:1fr}.dqc-fx-side{position:static}.dqc-fx-scroll{max-height:300px}}
`;

/* one visual scroll per page: inner scroll areas keep working, but with thin, unobtrusive scrollbars */
const THIN_SCROLL = `
/* every popup header on the DQ pages in the Accenture template (knowledge / values / policies / field values /
   sample rows / import / help / rail popover) */
.dqc-pop-h,.fv-h,.imp-h,.hk-head,.sr-h,.sr-hd,.sr-head,.dqc-rail-poph{
  background:#000 linear-gradient(90deg,#460073 0%,#7500C0 55%,#A100FF 100%) !important;color:#fff !important;
  box-shadow:inset 0 -3px 0 #A100FF}
.dqc-pop-h .x,.fv-h button,.imp-h button,.hk-head button{color:#fff !important}
.dqc-pop-h .sub{color:#e9d7ff !important}
.dqc-techlink{display:flex;justify-content:flex-end;margin:-4px 0 6px}.dqc-techlink button{all:unset;cursor:pointer;color:#7500C0;font-size:12.5px;font-weight:600}
.dqc-techlink button:hover{text-decoration:underline}
.rd2-over .vh-scrim,.rd2-over .fv-scrim{z-index:2500}
.dqc-fx-tbtn.dim{opacity:.45}
.dqc-fx-card .rv-mini,.dqc-fx-fcard .rv-mini{padding:0 2px}
.dqc-fx-scroll,.dqc-modal,.dqc-pop,.rs2 .conv,.rs2 .pane{scrollbar-width:thin;scrollbar-color:#cfd3da transparent}
.dqc-fx-scroll::-webkit-scrollbar,.dqc-modal::-webkit-scrollbar,.dqc-pop::-webkit-scrollbar,.rs2 .conv::-webkit-scrollbar,.rs2 .pane::-webkit-scrollbar{width:6px}
.dqc-fx-scroll::-webkit-scrollbar-thumb,.dqc-modal::-webkit-scrollbar-thumb,.dqc-pop::-webkit-scrollbar-thumb,.rs2 .conv::-webkit-scrollbar-thumb,.rs2 .pane::-webkit-scrollbar-thumb{background:#cfd3da;border-radius:3px}
`;
